import { useEffect, useMemo, useState, type RefObject } from 'react'
import { useLang } from '../i18n'
import { findTextRanges } from '../lib/page-search'

interface SettingsSearchProps {
  /** The scrolling pane whose currently-rendered section is searched — AccountSettingsPage's
   *  `.settings-main`. Content there swaps (AI agent ⇄ Appearance) and loads async (agent
   *  detection); a MutationObserver, not just the query, is what triggers a re-search. */
  containerRef: RefObject<HTMLDivElement | null>
}

/**
 * The Settings page's page-search field (top-right of its title row) — a plain, case-insensitive
 * substring search over whatever text is currently rendered in the section pane
 * (lib/page-search.ts walks live DOM text; this page has no document model behind it, unlike the
 * editor's Ctrl+F, see FindReplace.tsx). Up/Down cycle through matches, wrapping at either end,
 * and scroll the current one into view.
 *
 * The match set is drawn with the CSS Custom Highlight API (`::highlight()` in styles.css) rather
 * than DOM-inserted `<mark>` elements — a Range-based overlay that never mutates the section's own
 * markup, so it can't fight React's re-render of whatever is underneath it. Browsers without the
 * API (checked with a plain feature test) still get working navigation, just no visible highlight.
 */
export function SettingsSearch({ containerRef }: SettingsSearchProps) {
  const { t } = useLang()
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const observer = new MutationObserver(() => setVersion((v) => v + 1))
    observer.observe(root, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [containerRef])

  const matches = useMemo(
    () => {
      const root = containerRef.current
      return root ? findTextRanges(root, query) : []
      // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` stands in for "the section's rendered content may have changed"
    },
    [containerRef, query, version],
  )

  // Restart navigation at the first match whenever the query (or the content underneath) changes.
  useEffect(() => {
    setIndex(0)
  }, [query, version])

  const clampedIndex = matches.length ? ((index % matches.length) + matches.length) % matches.length : 0

  // Draw the match set + the current one, and clear both on empty/unmount — this overlay must
  // never outlive the query it belongs to.
  useEffect(() => {
    const supportsHighlight = typeof Highlight !== 'undefined' && typeof CSS !== 'undefined' && 'highlights' in CSS
    if (!supportsHighlight) return
    if (matches.length === 0) {
      CSS.highlights.delete('settings-search')
      CSS.highlights.delete('settings-search-current')
      return
    }
    CSS.highlights.set('settings-search', new Highlight(...matches))
    const current = matches[clampedIndex]
    if (current) CSS.highlights.set('settings-search-current', new Highlight(current))
    return () => {
      CSS.highlights.delete('settings-search')
      CSS.highlights.delete('settings-search-current')
    }
  }, [matches, clampedIndex])

  // Keep the active match in view — same "scroll the target element, not the range itself"
  // approach as FindReplace's editor-side navigation.
  useEffect(() => {
    const current = matches[clampedIndex]
    current?.startContainer.parentElement?.scrollIntoView({ block: 'center' })
  }, [matches, clampedIndex])

  function goNext(): void {
    if (matches.length) setIndex((i) => i + 1)
  }
  function goPrev(): void {
    if (matches.length) setIndex((i) => i - 1)
  }

  return (
    <div className="settings-search" role="search">
      <input
        className="fr-input settings-search-input"
        type="text"
        placeholder={t('settings_search_ph')}
        aria-label={t('settings_search_ph')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          if (e.shiftKey) goPrev()
          else goNext()
        }}
      />
      <button className="fmtbtn" title={t('fr_prev')} aria-label={t('fr_prev')} disabled={matches.length === 0} onClick={goPrev}>↑</button>
      <button className="fmtbtn" title={t('fr_next')} aria-label={t('fr_next')} disabled={matches.length === 0} onClick={goNext}>↓</button>
    </div>
  )
}
